import { useMemo, useState } from "react";

import useCurrentStateAndParamsWithFixedDomain from "app/pages/sales-intelligence/modules/company-analysis/hooks/useCurrentStateAndParamsWithFixedDomain";
import { useDigitalAssetsQuery } from "app/pages/sales-intelligence/modules/company-analysis/queries/useDigitalAssetsQuery";
import type {
    DigitalAssetType,
    DigitalAssetsRecordsMap,
} from "app/pages/sales-intelligence/modules/company-analysis/types/digital-assets";

const PAGE_SIZE = 20;
const EMPTY_DATA: never[] = [];

type UseFilteredDigitalAssetsDataConfig<T extends DigitalAssetType> = {
    assetType: T;
    getSearchValue: (item: DigitalAssetsRecordsMap[T]) => string;
    withTotal?: boolean;
};

export const useFilteredDigitalAssetsData = <T extends DigitalAssetType>({
    assetType,
    withTotal,
    getSearchValue,
}: UseFilteredDigitalAssetsDataConfig<T>) => {
    const {
        params: { key: domain },
    } = useCurrentStateAndParamsWithFixedDomain();

    const [page, setPage] = useState(1);
    const [searchText, setSearchText] = useState("");

    const { data = EMPTY_DATA, isLoading, isFetched } = useDigitalAssetsQuery(domain, assetType, withTotal);
    const isEmpty = data.length === 0 && isFetched;

    const displayedData = useMemo(() => {
        const cleanValue = searchText.trim().toLowerCase();

        if (!cleanValue) {
            return data;
        }

        return data.filter((item) => getSearchValue(item).toLowerCase().includes(cleanValue));
    }, [data, searchText, getSearchValue]);

    const paginationProps = useMemo(
        () => ({
            pageSize: PAGE_SIZE,
            total: displayedData.length,
            current: page,
            onChange(_currentPage: number, _pageSize: number, nextPage: number) {
                setPage(nextPage);
            },
        }),
        [displayedData.length, page],
    );

    const onSearchChange = (value: string) => {
        setSearchText(value);
        setPage(1);
    };

    return {
        displayedData,
        isLoading,
        totalCount: displayedData.length,
        onSearchChange,
        paginationProps,
        isEmpty,
        showSearch: !isLoading && data.length > 1,
    };
};
